<template>
  <div class="additional-ancillaries">
    <content-section :title="title" :description="description">
      <div
        v-for="(traveler, index) in travelers_with_eligibility"
        :key="`${step_id}:${traveler.id}`"
        class="additional-ancillaries__block"
      >
        <div class="additional-ancillaries__traveler">
          <traveler-label :traveler_info="traveler" use_name />
        </div>
        <div
          v-for="group in traveler.groups"
          :key="`${step_id}:${traveler.id}:${group.segmentId}`"
          class="additional-ancillaries__group"
        >
          <div class="additional-ancillaries__group-segment">
            {{ segment_label(group.segmentId) }}
          </div>
          <div class="additional-ancillaries__options">
            <ancillary-option
              v-for="item in group.items"
              :key="item.compound_key"
              :title="item.title"
              :subtitle="item.subtitle"
              :price_amount="item.priceAmount"
              :currency_symbol="item.currencySymbol"
              :icon="item.icon"
              :selected="is_picked(traveler.id, item.compound_key)"
              @select="pick(traveler.id, item.compound_key)"
            />
          </div>
        </div>
        <hr v-if="index < travelers_with_eligibility.length - 1" />
      </div>
    </content-section>
  </div>
</template>

<script>
import AncillaryOption from "./ancillary-option.vue";
import TravelerLabel from "@liveflight/components/blocks/traveler/traveler-label.vue";
import ContentSection from "@liveflight/components/blocks/wrappers/content-section.vue";
import { formatSegmentLabel } from "@liveflight/utilities";
import { getSegmentsFromFlightOffers } from "@liveflight/utilities/seat-map";
import {
  group_catalog_by_traveler_and_segment,
  update_traveler_selection,
} from "@liveflight/utilities/additional-services";

// Generic ancillary picker — same visual structure for bags, services,
// and sports equipment. Toggle each item independently; intra-step
// exclusivity is encoded by `incompatibleWith` on the catalog entry
// (and bundled picks by `combineWith`).
export default {
  name: "additional-ancillaries",
  components: {
    "traveler-label": TravelerLabel,
    "ancillary-option": AncillaryOption,
    "content-section": ContentSection,
  },
  props: {
    catalog: {
      type: Object,
      required: true,
    },
    travelers: {
      type: Array,
      required: true,
    },
    flight_offer: {
      type: Object,
      required: true,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    default_icon: {
      type: String,
      default: "luggage",
    },
    step_id: {
      type: String,
      default: "ancillary",
    },
    description: String,
    title: String,
  },
  computed: {
    travelers_with_eligibility() {
      const grouped = group_catalog_by_traveler_and_segment(
        this.catalog,
        this.travelers,
        this.flight_offer,
        this.default_icon,
      );
      return grouped.filter((traveler) => (traveler.groups || []).length > 0);
    },
    segments_map() {
      return getSegmentsFromFlightOffers([this.flight_offer]);
    },
    first_traveler() {
      return this.travelers_with_eligibility[0];
    },
  },
  methods: {
    segment_label(segment_id) {
      const segment = this.segments_map[segment_id];
      return segment ? formatSegmentLabel(segment, true) : segment_id;
    },
    is_picked(traveler_id, compound_key) {
      const per_traveler = this.value[traveler_id] || {};
      return Number(per_traveler[compound_key]) > 0;
    },
    resolve_catalog_keys_for_id(ref) {
      if (this.catalog[ref]) return [ref];
      return Object.keys(this.catalog).filter(
        (k) => (this.catalog[k] || {}).id === ref,
      );
    },
    keys_to_mutate_for(compound_key) {
      const { id: entry_id, requiredOnAllSegments = false } = this.catalog[compound_key] || {};
      if (!requiredOnAllSegments) return [compound_key];
      return Object.keys(this.catalog).filter(
        (k) => (this.catalog[k] || {}).id === entry_id,
      );
    },
    // combineWith partners inherit the same picking semantics — their own
    // requiredOnAllSegments / incompatibleWith must apply too. visited
    // guards a potential A↔B combineWith cycle.
    apply_pick(per_traveler, compound_key, visited) {
      if (visited[compound_key]) return;
      visited[compound_key] = true;
      this.keys_to_mutate_for(compound_key).forEach((k) => { per_traveler[k] = 1; });
      const { incompatibleWith = [], combineWith = [] } = this.catalog[compound_key] || {};
      incompatibleWith.forEach((ref) => {
        this.resolve_catalog_keys_for_id(ref).forEach((k) => { delete per_traveler[k]; });
      });
      combineWith.forEach((ref) => {
        this.resolve_catalog_keys_for_id(ref).forEach((k) => this.apply_pick(per_traveler, k, visited));
      });
    },
    apply_clear(per_traveler, compound_key, visited) {
      if (visited[compound_key]) return;
      visited[compound_key] = true;
      this.keys_to_mutate_for(compound_key).forEach((k) => { delete per_traveler[k]; });
      const { combineWith = [] } = this.catalog[compound_key] || {};
      combineWith.forEach((ref) => {
        this.resolve_catalog_keys_for_id(ref).forEach((k) => this.apply_clear(per_traveler, k, visited));
      });
    },
    pick(traveler_id, compound_key) {
      this.$emit(
        "input",
        update_traveler_selection(this.value, traveler_id, (per_traveler) => {
          const already_picked = Number(per_traveler[compound_key]) > 0;
          if (already_picked) {
            this.apply_clear(per_traveler, compound_key, {});
          } else {
            this.apply_pick(per_traveler, compound_key, {});
          }
        }),
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.additional-ancillaries {
  &__block {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__traveler {
    display: flex; 
    gap: 8px;
  }

  &__group-segment {
    font-weight: $vb-font-weight-semibold;
    color: $gray-700;
    margin-bottom: 8px;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
}
</style>
